import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Scissors } from "lucide-react";

const CHART =
  "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/hm88a09a_d0c39525-b6df-4450-aa12-42dcac53db9b.jpg";

const MILANGE_VIDEO =
  "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/fkqt7gf7_COTTON%20%20MILANGE%20VIDEO.mp4";

const MILANGE_POSTER =
  "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/xqeqokqe_COTTON%20MILANGE.jpeg";

const OPPO_POLO =
  "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/nga7vw36_COTTON%20POLO%20BLACK.jpeg";

const specs = [
  "220 GSM",
  "Cotton Pique",
  "Tipped Collar",
  "Embroidery Ready",
  "25 Shades",
  "Sizes S – 5XL",
];

export default function Polos() {
  return (
    <section
      id="polos"
      className="border-b-4 border-black py-20 lg:py-28 bg-[#F0F0F0]"
      data-testid="polos-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        {/* Header */}
        <div className="grid grid-cols-12 gap-8 mb-12 lg:mb-16">
          <div className="col-span-12 lg:col-span-7">
            <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#555] mb-4">
              /// Cotton polos · Pique line
            </div>
            <h2 className="font-heading uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              <span className="text-[#FF0055]">Polo up.</span>
              <br />
              <span className="bg-[#00F0FF] nb-border-2 px-2 inline-block">25 shades,</span>{" "}
              one collar.
            </h2>
            <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#0A0A0A]/80">
              Heavy-feel 220 GSM cotton pique with a contrast-tipped collar —
              corporate-uniform grade. Add your brand via computerised
              embroidery (200+ thread shades), screen-print or sublimation.
              Trusted by teams at OPPO, ATHLS and dozens of Mumbai brands.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-5 flex flex-wrap items-end gap-2 content-end">
            {specs.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1.5 nb-border-2 bg-white px-3 py-1.5 font-heading uppercase text-xs tracking-wider"
                data-testid={`polo-spec-${s.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Check size={12} strokeWidth={3} className="text-[#FF0055]" />
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Colour chart hero */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative nb-border-4 nb-shadow-lg overflow-hidden bg-white"
          data-testid="polos-colour-chart"
        >
          <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 bg-[#FFE600] nb-border-2 px-3 py-1.5 font-heading uppercase text-xs tracking-wider">
            <Scissors size={12} strokeWidth={3} /> 25 Shades · 220 GSM
          </div>
          <img
            src={CHART}
            alt="Customade cotton pique polo colour chart — 25 shades"
            className="w-full h-auto block"
            loading="lazy"
          />
        </motion.div>

        {/* Detail grid */}
        <div className="mt-12 grid grid-cols-12 gap-5 md:gap-6">
          {/* Embroidery showcase */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="col-span-12 md:col-span-7 nb-tile nb-border-4 nb-shadow-sm bg-white aspect-[16/10] md:aspect-auto md:min-h-[420px]"
            data-testid="polo-embroidery-tile"
          >
            <img
              src={OPPO_POLO}
              alt="Black cotton pique polo with green tipped collar and OPPO logo embroidery"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute top-3 left-3 nb-border-2 bg-[#0A0A0A] text-[#FFE600] px-3 py-1.5 font-heading uppercase text-xs tracking-wider">
              Embroidery · OPPO
            </div>
            <div className="absolute bottom-3 left-3 right-3 nb-border-2 bg-white px-3 py-2 font-heading uppercase text-xs tracking-wider flex items-center justify-between">
              <span>Logo embroidery on front · 6 thread colours</span>
              <span className="text-[#FF0055]">Pro</span>
            </div>
          </motion.div>

          {/* Milange video */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="col-span-12 md:col-span-5 nb-tile nb-border-4 nb-shadow-sm bg-white aspect-[16/10] md:aspect-auto md:min-h-[420px]"
            data-testid="polo-milange-tile"
          >
            <video
              src={MILANGE_VIDEO}
              poster={MILANGE_POSTER}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
              aria-label="Light grey melange cotton pique polo with green collar tipping and ATHLS embroidery — fabric texture"
            />
            <div className="absolute top-3 left-3 nb-border-2 bg-[#FF0055] text-white px-3 py-1.5 font-heading uppercase text-xs tracking-wider">
              Fabric · Milange
            </div>
            <div className="absolute bottom-3 left-3 right-3 nb-border-2 bg-[#FFE600] px-3 py-2 font-heading uppercase text-xs tracking-wider">
              Soft heathered finish · breathable
            </div>
          </motion.div>
        </div>

        {/* Footer CTA */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 nb-border-4 bg-[#FF0055] text-white p-5 md:p-7">
          <div>
            <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#FFE600] mb-1">
              Corporate &amp; events
            </div>
            <div className="font-heading uppercase text-xl md:text-2xl leading-tight">
              MOQ 25 pcs · Embroidery, sublimation &amp; screen-print available.
            </div>
          </div>
          <a href="#contact" className="nb-btn nb-btn-black" data-testid="polos-cta">
            Quote my polos <ArrowRight size={18} strokeWidth={3} />
          </a>
        </div>
      </div>
    </section>
  );
}
